"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePositionHistory = void 0;
const utils_1 = require("../../utils");
const react_1 = require("react");
const trade_utils_lib_1 = require("trade_utils_lib");
const FutureAPI_1 = require("../../apis/FutureAPI");
const interface_1 = require("../../interface");
function mergeTimeRange(filterRange, lastTime) {
    return lastTime ? Object.assign(Object.assign({}, filterRange), { endTime: lastTime }) : filterRange;
}
function processHistoryPosition(value) {
    const { qty_long, qty_short = "", amount_long = "", amount_short = "", amount_settle = "", leverage, position_side } = value;
    const isLong = position_side === interface_1.FuturePositionSide.LONG;
    const positionQty = new trade_utils_lib_1.SafeDecimal(isLong ? qty_long : qty_short);
    const positionSide = isLong ? 1 : -1;
    const positionQtyABS = positionQty.abs();
    const avgShort = new trade_utils_lib_1.SafeDecimal(amount_short).div(qty_short).mul(-1);
    const avgLong = new trade_utils_lib_1.SafeDecimal(amount_long).div(qty_long).mul(-1);
    const [costAvgPrice, avgPrice] = isLong ? [avgLong, avgShort] : [avgShort, avgLong];
    const amountSettle = new trade_utils_lib_1.SafeDecimal(amount_settle).mul(-1);
    const realizedPnlRate = avgPrice
        .sub(costAvgPrice)
        .div(costAvgPrice)
        .mul(leverage || 1)
        .mul(positionSide);
    return Object.assign(Object.assign({}, value), { realizedPnl: amountSettle.toString(), realizedPnlRate: realizedPnlRate.toString(), positionQuantity: positionQtyABS.toString(), costAvgPrice: trade_utils_lib_1.NumberCommonUtils.toNormalPrice(costAvgPrice), avgPrice: trade_utils_lib_1.NumberCommonUtils.toNormalPrice(avgPrice) });
}
const PositionHistoryModelsPageLength = 12;
/**
 * 获取合约历史仓位列表，支持refresh跟loadMore
 */
const usePositionHistory = (props) => {
    const { symbol, positionFlag, autoRefresh = true, timeRange } = props;
    const [hasMore, setHasMore] = (0, react_1.useState)(true);
    const [positions, setPositions] = (0, react_1.useState)();
    const [error, setError] = (0, react_1.useState)();
    const [refreshing, setRefreshing] = (0, react_1.useState)(true);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const autoRefreshRef = (0, react_1.useRef)(autoRefresh);
    autoRefreshRef.current = autoRefresh;
    const endTime = (0, react_1.useMemo)(() => {
        if (positions === null || positions === void 0 ? void 0 : positions.length) {
            return positions[positions.length - 1].updated_time;
        }
        return undefined;
    }, [positions]);
    const loadingRef = (0, react_1.useRef)(false);
    /**校准之后的时间区间，结束时间需要加一天 */
    const timeRangeAdjusted = (0, react_1.useMemo)(() => (0, utils_1.adjustTimeRange)(timeRange), [timeRange]);
    const queryPositions = (0, react_1.useCallback)((lastTime) => {
        const request = { limit: PositionHistoryModelsPageLength };
        symbol && (request.symbol = symbol);
        // 设置按照时间筛选的参数
        const range = mergeTimeRange(timeRangeAdjusted, lastTime);
        if (range) {
            range.endTime && (request.end_time = Number(range.endTime));
            range.startTime && (request.start_time = Number(range.startTime));
        }
        if (positionFlag) {
            request.position_flag = positionFlag;
        }
        return (0, FutureAPI_1.queryHistoryPositions)(request);
    }, [positionFlag, symbol, timeRangeAdjusted]);
    const refresh = (0, react_1.useCallback)(() => {
        setRefreshing(true);
        loadingRef.current = true;
        return queryPositions().subscribe((data) => {
            setRefreshing(false);
            setError(null);
            // fix 避免后端返回null时报错
            const positionList = data.positions || [];
            setHasMore(positionList.length >= PositionHistoryModelsPageLength);
            setPositions(positionList.map(processHistoryPosition));
            loadingRef.current = false;
        }, (error) => {
            setRefreshing(false);
            setError(error);
            setHasMore(false);
            loadingRef.current = false;
        });
    }, [queryPositions]);
    const loadMore = (0, react_1.useCallback)(() => {
        if (!queryPositions || !endTime || !hasMore || !(positions === null || positions === void 0 ? void 0 : positions.length) || loadingRef.current) {
            return;
        }
        loadingRef.current = true;
        setLoading(true);
        return queryPositions(endTime).subscribe((data) => {
            setError(null);
            if (data) {
                const newPositions = data.positions || [];
                setHasMore(newPositions.length >= PositionHistoryModelsPageLength);
                setPositions((v) => {
                    return v === null || v === void 0 ? void 0 : v.concat(newPositions.map(processHistoryPosition));
                });
            }
            loadingRef.current = false;
            setLoading(false);
        }, (_error) => {
            setHasMore(false);
            loadingRef.current = false;
            setLoading(false);
        });
    }, [queryPositions, endTime, hasMore, positions === null || positions === void 0 ? void 0 : positions.length]);
    // 监听关键信息的变跟，并刷新页面
    (0, react_1.useEffect)(() => {
        if (!autoRefreshRef.current) {
            return;
        }
        const sub = refresh();
        return () => sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionFlag, symbol, timeRangeAdjusted]);
    return { positions, refreshing, loading, error, hasMore, refresh, loadMore };
};
exports.usePositionHistory = usePositionHistory;
